/**
 * `store/session` — the session slice of `spec.md` §13.9: token, user, expiry, admin flag.
 *
 * **The store is a mirror, not the owner.** `lib/api.ts` owns the token lifecycle
 * (§9.2: minting, the 80%-of-TTL refresh, the single `SESSION_EXPIRED` re-exchange)
 * because the request path is the only place that can react in time. This slice
 * subscribes to the client and re-publishes what it sees, so React can render "who am
 * I" and "are we authenticated yet" without any component reaching for the token.
 *
 * That direction of dependency is deliberate and one-way: `store/*` imports `lib/api`,
 * never the reverse. A store that owned the token would have to be imported by the
 * client, and the resulting cycle is exactly what `import-x/no-cycle` forbids.
 *
 * Every **data** field here is JSON-serialisable (§13.9's "keep them serialisable"), so
 * {@link selectSessionSnapshot} can be handed straight to a persistence tier. Derived
 * values — `isAdmin`, `isAuthenticated`, the display name — are **selectors**, not
 * duplicated state: a second copy of `user.isAdmin` is a second thing to keep in sync.
 */

import { create } from 'zustand';

import type { ServerInfo, SessionUser } from '@bl/shared';

import type { ApiClient, ApiFailure, SessionSnapshot } from '../lib/api';
import { api, toApiFailure } from '../lib/api';

/**
 * `idle` → nothing tried yet. `authenticating` covers both the first exchange and a
 * refresh, because the UI treats them identically: the shell is up, the data is not.
 */
export type SessionStatus = 'idle' | 'authenticating' | 'ready' | 'error';

/** The serialisable half of the slice. */
export interface SessionData {
  status: SessionStatus;
  /** Mirrored from the client. Read by nothing but diagnostics — requests carry it. */
  token: string | null;
  /** Unix **seconds**. `0` when there is no session. */
  expiresAt: number;
  user: SessionUser | null;
  /** `indexReady` / `trackCount` / `fts5` — facts the client cannot infer (§6.3). */
  server: ServerInfo | null;
  /** The `startapp` payload of this launch, or `null`. */
  startParam: string | null;
  error: ApiFailure | null;
}

export interface SessionActions {
  /** Exchange `initData`. Resolves `true` on success; the failure is in `error`. */
  authenticate(): Promise<boolean>;
  /** Extend the session from a still-valid token. */
  refresh(): Promise<boolean>;
  /** Adopt a client snapshot. Called by the client subscription; exposed for tests. */
  adopt(snapshot: SessionSnapshot): void;
  /** Forget the session (blocked user, or a deliberate re-launch). */
  reset(): void;
  /** Stop mirroring the client. Tests use it; the app never needs to. */
  detach(): void;
}

export type SessionState = SessionData & SessionActions;

const EMPTY: SessionData = {
  status: 'idle',
  token: null,
  expiresAt: 0,
  user: null,
  server: null,
  startParam: null,
  error: null,
};

export interface SessionStoreDeps {
  client?: ApiClient;
}

/**
 * Build an independent session store.
 *
 * A factory rather than a bare `create` call so a test can hold two stores over two
 * fake clients at once, which is the only way to assert that nothing leaks through
 * module state.
 */
export function createSessionStore(deps: SessionStoreDeps = {}) {
  const client = deps.client ?? api;

  return create<SessionState>()((set, get) => {
    // Held in the initializer's closure: the store has no field for it, so it can never
    // be serialised or rendered by mistake.
    let unsubscribe: (() => void) | null = client.onSession((snapshot) => {
      get().adopt(snapshot);
    });

    const fail = (error: unknown): false => {
      set({ status: 'error', error: toApiFailure(error) });
      return false;
    };

    return {
      ...EMPTY,

      adopt(snapshot: SessionSnapshot): void {
        // A cleared session must not be reported as `ready`: the shell has to fall back
        // to the launch guard rather than render an authenticated UI with no token.
        if (snapshot.token === null) {
          set({
            status: get().status === 'error' ? 'error' : 'idle',
            token: null,
            expiresAt: 0,
            user: null,
            server: null,
          });
          return;
        }
        set({
          status: 'ready',
          token: snapshot.token,
          expiresAt: snapshot.expiresAt,
          user: snapshot.user,
          server: snapshot.server,
          startParam: snapshot.startParam,
          error: null,
        });
      },

      async authenticate(): Promise<boolean> {
        set({ status: 'authenticating', error: null });
        try {
          await client.authenticate();
          return true;
        } catch (error) {
          return fail(error);
        }
      },

      async refresh(): Promise<boolean> {
        set({ status: 'authenticating', error: null });
        try {
          await client.refresh();
          return true;
        } catch (error) {
          return fail(error);
        }
      },

      reset(): void {
        client.clearSession();
        set({ ...EMPTY });
      },

      detach(): void {
        if (unsubscribe === null) return;
        unsubscribe();
        unsubscribe = null;
      },
    };
  });
}

/** The app-wide session slice. */
export const useSessionStore = createSessionStore();

/* ────────────────────────────────────────────────────────────────────────────
 * Selectors — derived values live here, never in state
 * ──────────────────────────────────────────────────────────────────────────── */

/** §9.6: admin is an attribute of a Telegram identity, reported by the server. */
export const selectIsAdmin = (state: SessionData): boolean => state.user?.isAdmin === true;

export const selectIsAuthenticated = (state: SessionData): boolean =>
  state.status === 'ready' && state.token !== null;

/** `true` while the very first exchange is in flight — the boot splash condition. */
export const selectIsBooting = (state: SessionData): boolean =>
  state.status === 'authenticating' && state.user === null;

/**
 * What to print in the corner. `first_name` may legitimately be `''` (§6.3), and
 * `username` may be absent, so this can end up `null` — the caller omits the line
 * rather than printing a placeholder.
 */
export const selectDisplayName = (state: SessionData): string | null => {
  const user = state.user;
  if (user === null) return null;
  if (user.firstName.length > 0) return user.firstName;
  return user.username;
};

/** `true` once the server has said the catalogue is worth asking for (§6.4's 503). */
export const selectIndexReady = (state: SessionData): boolean => state.server?.indexReady === true;

export const selectTrackCount = (state: SessionData): number => state.server?.trackCount ?? 0;

/** `true` when search will use FTS5 rather than the `LIKE` fallback. */
export const selectFts5 = (state: SessionData): boolean => state.server?.fts5 === true;

/** The JSON-serialisable projection, for a persistence tier or a diagnostic dump. */
export const selectSessionSnapshot = (state: SessionData): SessionData => ({
  status: state.status,
  token: state.token,
  expiresAt: state.expiresAt,
  user: state.user,
  server: state.server,
  startParam: state.startParam,
  error: state.error,
});
